import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, X, Clock, Store, XCircle } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SellListing() {
  const [seller, setSeller] = useState(null);
  const load = () => api.get("/market/seller/me").then((r) => setSeller(r.data));
  useEffect(() => { load(); }, []);

  if (!seller) return <Loader full />;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {seller.status === "approved" ? <ListingForm seller={seller.seller} /> :
        seller.status === "pending" ? <Status icon={Clock} title="Solicitação em análise" text="Nossa equipe está avaliando sua conta de vendedor. Você será notificado assim que for aprovada." /> :
        <ApplyForm rejected={seller.status === "rejected"} onDone={load} />}
    </div>
  );
}

function Status({ icon: Icon, title, text }) {
  return (
    <div data-testid="seller-status-card" className="rounded-2xl border border-border bg-card p-10 text-center">
      <Icon className="mx-auto h-10 w-10 text-primary" />
      <h1 className="mt-4 text-2xl font-extrabold">{title}</h1>
      <p className="mt-2 text-muted-foreground">{text}</p>
    </div>
  );
}

function ApplyForm({ rejected, onDone }) {
  const [f, setF] = useState({ store_name: "", city: "", state: "", phone: "", about: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/market/seller/apply", f); toast.success("Solicitação enviada!"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4">
      <p className="eyebrow text-primary flex items-center gap-2"><Store className="h-4 w-4" /> Seja um vendedor</p>
      <h1 className="text-3xl font-extrabold tracking-tight">Abra sua loja no Mercado</h1>
      {rejected && <p className="flex items-center gap-2 rounded-xl bg-destructive/10 p-3 text-sm text-destructive" data-testid="seller-rejected"><XCircle className="h-4 w-4" /> Sua solicitação anterior foi recusada. Você pode enviar uma nova.</p>}
      <p className="text-sm text-muted-foreground">Para manter o Mercado confiável, todo vendedor passa por aprovação da equipe PaleteLab.</p>
      <div><Label>Nome da loja *</Label><Input data-testid="apply-store-name" value={f.store_name} onChange={set("store_name")} required className="mt-1.5 rounded-xl" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2"><Label>Cidade *</Label><Input data-testid="apply-city" value={f.city} onChange={set("city")} required className="mt-1.5 rounded-xl" /></div>
        <div><Label>UF *</Label><Input data-testid="apply-state" maxLength={2} value={f.state} onChange={(e) => setF((x) => ({ ...x, state: e.target.value.toUpperCase() }))} required className="mt-1.5 rounded-xl" /></div>
      </div>
      <div><Label>Telefone / WhatsApp *</Label><Input data-testid="apply-phone" value={f.phone} onChange={set("phone")} required minLength={8} className="mt-1.5 rounded-xl" /></div>
      <div><Label>Sobre você e seu trabalho</Label><Textarea data-testid="apply-about" value={f.about} onChange={set("about")} className="mt-1.5 rounded-xl" /></div>
      <Button type="submit" data-testid="apply-submit" disabled={busy} className="rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar solicitação"}</Button>
    </form>
  );
}

const EMPTY = { title: "", description: "", kind: "movel", category: "", condition: "novo", price: "", stock: "1", images: [], city: "", state: "", delivery: false, delivery_fee: "", pickup: true, dimensions: "" };

function ListingForm({ seller }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ ...EMPTY, city: seller?.city || "", state: seller?.state || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    api.get("/market/categories").then((r) => setCats(r.data));
    if (id) api.get(`/market/listings/${id}`).then(({ data }) => setF({ ...EMPTY, ...data, price: String(data.price), stock: String(data.stock), delivery_fee: String(data.delivery_fee || "") }));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Escolha uma categoria");
    if (!f.images.length) return toast.error("Envie pelo menos uma foto");
    setBusy(true);
    const body = { ...f, price: parseFloat(String(f.price).replace(",", ".")), stock: parseInt(f.stock, 10), delivery_fee: parseFloat(String(f.delivery_fee).replace(",", ".")) || 0 };
    ["id", "seller", "status", "is_owner", "seller_id", "reject_reason", "sold_count", "created_at", "updated_at"].forEach((k) => delete body[k]);
    try {
      const { data } = id ? await api.put(`/market/listings/${id}`, body) : await api.post("/market/listings", body);
      toast.success("Anúncio enviado para análise!");
      navigate(`/mercado/anuncio/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <p className="eyebrow text-primary">Mercado</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">{id ? "Editar anúncio" : "Novo anúncio"}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Os anúncios passam por moderação antes de aparecer no Mercado.</p>
      </div>
      <Card>
        <div className="grid grid-cols-2 gap-2">
          {[["movel", "Móvel pronto"], ["material", "Material / paletes"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`form-kind-${v}`} onClick={() => setF((x) => ({ ...x, kind: v, category: "" }))}
              className={`rounded-xl border-2 p-3 text-sm font-bold ${f.kind === v ? "border-primary bg-primary/5" : "border-border"}`}>{l}</button>
          ))}
        </div>
        <div><Label>Título *</Label><Input data-testid="form-title" value={f.title} onChange={set("title")} required minLength={3} className="mt-1.5 rounded-xl" /></div>
        <div><Label>Descrição *</Label><Textarea data-testid="form-description" value={f.description} onChange={set("description")} required minLength={10} className="mt-1.5 min-h-[120px] rounded-xl" /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Categoria *</Label>
            <Select value={f.category} onValueChange={(v) => setF((x) => ({ ...x, category: v }))}>
              <SelectTrigger data-testid="form-category" className="mt-1.5 rounded-xl"><SelectValue placeholder="Escolha" /></SelectTrigger>
              <SelectContent>{cats.filter((c) => c.kind === f.kind).map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Condição</Label>
            <Select value={f.condition} onValueChange={(v) => setF((x) => ({ ...x, condition: v }))}>
              <SelectTrigger data-testid="form-condition" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="novo">Novo</SelectItem><SelectItem value="usado">Usado / reaproveitado</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><Label>Preço (R$) *</Label><Input data-testid="form-price" inputMode="decimal" value={f.price} onChange={(e) => setF((x) => ({ ...x, price: e.target.value.replace(/[^\d.,]/g, "") }))} required className="mt-1.5 rounded-xl" /></div>
          <div><Label>Estoque *</Label><Input data-testid="form-stock" type="number" min={1} value={f.stock} onChange={set("stock")} required className="mt-1.5 rounded-xl" /></div>
          <div><Label>Medidas</Label><Input data-testid="form-dimensions" value={f.dimensions} onChange={set("dimensions")} placeholder="100×50×40 cm" className="mt-1.5 rounded-xl" /></div>
        </div>
      </Card>
      <Card><Label>Fotos * (até 10)</Label><Photos images={f.images} onChange={(images) => setF((x) => ({ ...x, images }))} /></Card>
      <Card>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><Label>Cidade *</Label><Input data-testid="form-city" value={f.city} onChange={set("city")} required className="mt-1.5 rounded-xl" /></div>
          <div><Label>UF *</Label><Input data-testid="form-state" maxLength={2} value={f.state} onChange={(e) => setF((x) => ({ ...x, state: e.target.value.toUpperCase() }))} required className="mt-1.5 rounded-xl" /></div>
        </div>
        <label className="flex items-center justify-between rounded-xl border border-border p-3"><span className="text-sm font-medium">Permitir retirada no local</span><Switch data-testid="form-pickup" checked={f.pickup} onCheckedChange={(v) => setF((x) => ({ ...x, pickup: v }))} /></label>
        <label className="flex items-center justify-between rounded-xl border border-border p-3"><span className="text-sm font-medium">Fazer entrega</span><Switch data-testid="form-delivery" checked={f.delivery} onCheckedChange={(v) => setF((x) => ({ ...x, delivery: v }))} /></label>
        {f.delivery && <div><Label>Taxa de entrega (R$)</Label><Input data-testid="form-delivery-fee" inputMode="decimal" value={f.delivery_fee} onChange={(e) => setF((x) => ({ ...x, delivery_fee: e.target.value.replace(/[^\d.,]/g, "") }))} placeholder="0 = grátis" className="mt-1.5 rounded-xl" /></div>}
      </Card>
      <Button type="submit" data-testid="form-submit" disabled={busy} className="h-12 w-full rounded-full text-base font-bold">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enviar para análise"}</Button>
    </form>
  );
}

const Card = ({ children }) => <div className="space-y-4 rounded-2xl border border-border bg-card p-5 sm:p-6">{children}</div>;

function Photos({ images, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - images.length);
    setBusy(true);
    const urls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); urls.push(data.url); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload"); }
    }
    onChange([...images, ...urls]);
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-3">
      {images.map((u, i) => (
        <div key={u} className="relative h-24 w-24 overflow-hidden rounded-xl border border-border">
          <img src={mediaUrl(u)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => onChange(images.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white" data-testid={`remove-photo-${i}`}><X className="h-3 w-3" /></button>
        </div>
      ))}
      {images.length < 10 && (
        <button type="button" data-testid="form-upload-btn" onClick={() => ref.current?.click()} disabled={busy}
          className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary">
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Upload className="h-6 w-6" /><span className="text-xs">Enviar foto</span></>}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={pick} data-testid="form-upload-input" />
    </div>
  );
}
